export type SubscriptionResponseMode = "lite" | "detailed";

export type SubscriptionEntitlements = {
  "jugl::board": number | null;
  "jugl::forms": number | null;
  "jugl::drive": number | null;
  "jugl::users": number | null;
  "jugl::board-ai-agent-lite": number | null;
  "jugl::calls": number | null;
  "jugl::customer": number | null;
  "jugl::dashboard": number | null;
  "jugl::invoice": number | null;
  "jugl::profile": number | null;
  "jugl::time-clock": number | null;
  "jugl::ai_credits": number | null;
  "jugl::tasks": number | null;
  "jugl::knowledge": number | null;
};

export type PendingInvoice = {
  description: string;
  invoice_id: string;
  invoice_url: string;
  subscription_id: string | null;
  type: "plan" | "addon" | "recharge" | null;
};

export type SubscriptionQuotaItem = {
  allowed: number;
  balance?: number;
  end_date: string;
  start_date: string;
  status: string;
  usage: number;
};

export type SubscriptionGetLiteResponse = {
  cancel_at: string | null;
  current_period_start: string;
  current_period_end: string;
  status: string;
  entitlements: SubscriptionEntitlements;
  pending_invoices?: PendingInvoice[];
  quota: Record<string, SubscriptionQuotaItem>;
  version: string;
};

export type PaymentMethod = {
  id: string;
  card: {
    display_brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
    funding: string;
  };
};

export type BillingDetails = {
  billing_cycle_anchor: string; // datetime
  currency: string;
  period_start: string; // datetime
  period_end: string; // datetime
};

export type PaymentDetails = {
  collection_method: string;
  default_payment_method: string | null;
  latest_invoice_id: string | null;
};

export enum PlanTierIndex {
  free = 0,
  starter = 1,
  pro = 2,
  business = 3,
}

export type PlanInfo = {
  kind: "plan";
  plan_id: string;
  name: string;
  description: string;
  version: string;
  type: string;
  meta_data: {
    idx: PlanTierIndex;
    stripe_prod_id: string;
  };
  default_addons: Array<{
    credits: number | null;
    id: string;
    type: string;
  }>;
};

export type AddonInfo = {
  kind: "addon";
  addon_name: string;
  addon_type: string;
  credits: number;
};

export type LineItemInfo = PlanInfo | AddonInfo;

export type LineItem = {
  price_id: string;
  product_id: string;
  subscription_item_id: string;
  type: string; // e.g. recurring
  currency: string;
  interval: string; // e.g. month
  interval_count: number;
  quantity: number;
  unit_amount: number; // Price per unit (in minor units, e.g. cents)
  total_amount: number; // Total for this line (in minor units)
  info: LineItemInfo;
};

export type SubscriptionDetails = {
  addons: Record<string, unknown>;
  plan: {
    status: string;
    subscription_id: string;
    currency: string;
    customer_id: string;
    current_period_start: string; // datetime
    current_period_end: string; // datetime
    cancel_at: string | null; // datetime
    canceled_at: string | null; // datetime
    ended_at: string | null; // datetime
    metadata: Record<string, unknown>;
    billing: BillingDetails;
    payment: PaymentDetails;
    line_items: LineItem[];
  };
};

export type SubscriptionGetDetailedResponse = {
  id: string;
  entity_id: string;
  status: string;
  created_at: string; // datetime
  updated_at: string; // datetime
  current_period_start: string; // datetime
  current_period_end: string; // datetime
  cancel_at: string | null; // datetime
  version: string;
  entitlements: SubscriptionEntitlements;
  payment_method: PaymentMethod;
  details: SubscriptionDetails;
};

export type Price = {
  id: string;
  currency: string;
  interval: string;
  interval_count: number;
  unit_amount: number;
};

export type Addon = {
  id: string;
  credits: number | null;
  type: "addon";
};

export type SupportedAddon = {
  id: string;
  credits: number | null;
  type?: string;
  meta_data: {
    stripe_prod_id: string;
  } | null;
  prices: Price[];
};

export type Plan = {
  id: string;
  name: string;
  description: string;
  version: string;
  meta_data: {
    stripe_prod_id: string;
    idx: PlanTierIndex;
  };
  prices: Price[];
  default_addons: Addon[];
  supported_addons: SupportedAddon[];
};

export type PlansResponse = {
  data: Plan[];
};

export type SupportedAddonsResponse = {
  data: SupportedAddon[];
};

export type CreateSubscriptionItem = {
  price_id: string;
  quantity: number;
};

export type CreateSubscriptionRequest = {
  items: CreateSubscriptionItem[];
};

export type ChangePlanRequest = {
  plan_id: string;
  price_id: string;
};

export type AdjustAddonRequest = {
  price_id: string;
  qty: number;
};

export type CreatePortalSessionRequest = {
  return_url: string;
};

export type CreatePortalSessionResponse = {
  url: string;
};

export type SubscriptionInvoiceLineItem = {
  line_item_id: string;
  description: string;
  amount: number;
  currency: string;
  quantity: number;
  product_id: string;
  subscription_id: string;
  period_start: number;
  period_end: number;
};

export type Invoice = {
  invoice_id: string;
  number: string | null;
  status: string;
  amount_paid: number;
  subtotal: number;
  total: number;
  currency: string;
  created: number;
  period_start: number;
  period_end: number;
  customer_id: string;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
  line_items: SubscriptionInvoiceLineItem[];
};

export type SubscriptionInvoicesResponse = {
  data: Invoice[];
  has_more: boolean;
};

export type TopupConfigResponse = {
  auto_recharge_amount: number;
  auto_recharge_threshold: number;
};

export type UpdateTopupConfigRequest = {
  auto_recharge_amount: number | null;
  auto_recharge_threshold: number | null;
};

export type TopupNowRequest = {
  amount: number;
};

export type TopupNowResponse = {
  invoice_url: string;
  status: "paid" | "open";
};

export type PreviewInvoiceResponse = {
  amount_due: number | null;
};

export type TestClockResponse = {
  created: number;
  deletes_after: number;
  frozen_time: string;
  id: string;
  livemode: boolean;
  name: string;
  object: string;
  status: string;
  status_details: Record<string, unknown>;
};
